import LogoFull from '@/v2/assets/images/pitaliy_logo-beta.svg';
import {
  AnalyticsIcon,
  AvatarIcon,
  CommentIcon,
  GuideIcon,
  HomeIcon,
  MessageIcon,
  PapersIcon,
  PublicPageSettingIcon,
  SendIcon,
  TalkIcon,
  UrlIcon,
} from '@/v2/components/icons';
import DesignSettingIcon from '@/v2/components/icons/DesignSettingIcon';
import { PATH } from '@/v2/utils/constants';
import { Flex, Space } from 'antd';
import React, { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Permission } from '@/commons/services/permissions.service';
import { useAtomValue } from 'jotai';
import { userInfoAtom } from '@/commons/utils/atoms';

const MANUAL_URL =
  'https://amenable-flute-8d3.notion.site/PITALIy-2ca2487b188780e19877f5e2ad4c75c4';

type SidebarItem = {
  id: string;
  icon: React.ReactElement<{ isActive?: boolean }>;
  text: string;
  path: string;
};

// Menu items with their required permissions
// If no permissions specified, item is always visible
const allMenuItems: (SidebarItem & { requiredPermissions?: Permission[] })[] = [
  {
    id: 'public-settings',
    icon: <PublicPageSettingIcon />,
    text: '公開設定',
    path: PATH.PUBLIC_SETTINGS,
    requiredPermissions: [Permission.BASIC_USAGE],
  },
  {
    id: 'chat-ui',
    icon: <TalkIcon />,
    text: 'テスト',
    path: PATH.CHAT_UI,
    requiredPermissions: [Permission.BASIC_USAGE, Permission.CONVERSATION_TEST],
  },
  {
    id: 'dashboard',
    icon: <HomeIcon />,
    text: 'ダッシュボード',
    path: PATH.DASHBOARD,
    requiredPermissions: [Permission.BASIC_USAGE],
  },
  {
    id: 'chat-settings',
    icon: <CommentIcon />,
    text: '会話設定',
    path: PATH.CHAT_SETTINGS,
    requiredPermissions: [Permission.BASIC_USAGE],
  },
  {
    id: 'avatar-settings',
    icon: <AvatarIcon />,
    text: 'アバター設定',
    path: PATH.AVATAR_SETTINGS,
    requiredPermissions: [Permission.BASIC_USAGE],
  },
  {
    id: 'design-settings',
    icon: <DesignSettingIcon />,
    text: 'デザイン設定',
    path: PATH.DESIGN_SETTINGS,
    requiredPermissions: [Permission.BASIC_USAGE],
  },
  {
    id: 'url-settings',
    icon: <UrlIcon />,
    text: 'リンク設定',
    path: PATH.URL_SETTINGS,
    requiredPermissions: [Permission.BASIC_USAGE, Permission.LINK_SETTING],
  },
  {
    id: 'analytics',
    icon: <AnalyticsIcon />,
    text: 'アナリティクス',
    path: PATH.ANALYTICS,
    requiredPermissions: [Permission.BASIC_USAGE, Permission.ANALYTICS],
  },
  {
    id: 'form-history',
    icon: <SendIcon />,
    text: 'フォーム回答履歴',
    path: PATH.FORM_HISTORY,
    requiredPermissions: [Permission.BASIC_USAGE, Permission.FORM_FUNCTION],
  },
  {
    id: 'conversation-history',
    icon: <PapersIcon />,
    text: '会話履歴',
    path: PATH.CONVERSATION_HISTORY,
    requiredPermissions: [Permission.BASIC_USAGE],
  },
  {
    id: 'faq-setting',
    icon: <MessageIcon />,
    text: 'FAQ設定',
    path: PATH.FAQ_SETTING,
    requiredPermissions: [Permission.BASIC_USAGE, Permission.FAQ_SETTING],
  },
];

/**
 * Find the active menu item based on current path
 * @param currentPath - Current pathname from location
 * @param menuItems - Array of sidebar menu items
 * @returns Active menu item ID or null if no match
 */
const getActiveItemId = (
  currentPath: string,
  menuItems: SidebarItem[],
): string | null => {
  // For DASHBOARD path ('/'), only match if path is exactly '/'
  // This prevents matching paths like '/accountsettings' with DASHBOARD
  if (currentPath === PATH.DASHBOARD) {
    const dashboardItem = menuItems.find(
      (item) => item.path === PATH.DASHBOARD,
    );
    return dashboardItem ? dashboardItem.id : null;
  }

  // For other paths, use startsWith to match nested routes
  // Sort by path length descending to match the most specific path first
  const matchingItems = menuItems
    .filter((item) => {
      // Skip DASHBOARD for non-root paths
      if (item.path === PATH.DASHBOARD) {
        return false;
      }
      // Use startsWith for nested routes (e.g., '/chatsettings/medialibrary' matches '/chatsettings')
      return currentPath.startsWith(item.path);
    })
    .sort((a, b) => b.path.length - a.path.length);

  return matchingItems.length > 0 ? matchingItems[0].id : null;
};

const SideBarLayout: React.FC = () => {
  const userInfo = useAtomValue(userInfoAtom);

  // Filter menu items based on permissions
  const filteredMenuItems = useMemo(() => {
    return allMenuItems.filter((item) => {
      if (!item.requiredPermissions) {
        return true;
      }

      return item.requiredPermissions.every((permission) =>
        userInfo?.permissions?.includes(permission),
      );
    });
  }, [userInfo?.permissions]);

  const location = useLocation();

  // Calculate active item ID based on current path
  const activeItemId = useMemo(
    () => getActiveItemId(location.pathname, filteredMenuItems),
    [location.pathname, filteredMenuItems],
  );

  const navigate = useNavigate();

  // Handler to navigate when menu item is clicked
  // activeItemId will be automatically updated via useMemo when location.pathname changes
  const handleMenuItemClick = (_id: string, path: string) => {
    navigate(path);
  };

  return (
    <Flex
      align="center"
      className="w-[227px] h-[96vh] flex-col !m-4 !pt-6 !pb-5 bg-white/30 border !border-white/50 rounded-3xl shadow-[0px_8px_16px_-8px_rgba(0,0,0,0.05)] backdrop-blur-[16px] transition-all duration-1000 ease-in-out"
    >
      {/* Logo section */}
      <Flex
        justify="center"
        align="center"
        className="!mb-12 self-stretch"
        onClick={() => {
          navigate(PATH.DASHBOARD);
        }}
      >
        <Flex align="center" justify="center">
          <div className="w-[187px] h-[31px]">
            <img src={LogoFull} alt="Logo" className="w-30 h-[31px] ml-2" />
          </div>
        </Flex>
      </Flex>

      {/* Navigation menu */}
      <Flex className="w-[187px] flex-col gap-[2px]">
        {filteredMenuItems.map((item) => {
          const isActive = item.id === activeItemId;
          const isPublicSettings = item.id === 'public-settings';
          const navItem = (
            <Flex
              align="center"
              className={`nav-item ${isActive ? 'active' : ''}`}
              onClick={() => handleMenuItemClick(item.id, item.path)}
              tabIndex={0}
              role="button"
              aria-pressed={isActive}
            >
              <Space className="nav-icon">
                {React.cloneElement(item.icon, { isActive })}
              </Space>
              <span className="nav-text">{item.text}</span>
            </Flex>
          );
          if (isPublicSettings) {
            return (
              <Flex key={item.id} vertical className="w-full" align="stretch">
                {navItem}
                <div
                  className="my-2 border-b border-[#E4E3EA]"
                  aria-hidden
                />
              </Flex>
            );
          }
          return <React.Fragment key={item.id}>{navItem}</React.Fragment>;
        })}
      </Flex>

      <Flex className="w-[187px] mt-auto pt-4">
        <Flex
          align="center"
          className="nav-item w-full"
          onClick={() => {
            window.open(MANUAL_URL, '_blank', 'noopener,noreferrer');
          }}
          tabIndex={0}
          role="button"
          aria-label="使い方マニュアル"
        >
          <Space className="nav-icon">
            <GuideIcon />
          </Space>
          <span className="nav-text">使い方マニュアル</span>
        </Flex>
      </Flex>
    </Flex>
  );
};

export default SideBarLayout;
